<script lang="ts" setup>
import { i18n } from '@/i18n';
import { computed, onMounted, ref, watch } from 'vue';
import { filterStringNumberUndefinedToString } from '@/coreDomainsModule/extends/string';
import AppDatePicker from '@/corePagesModule/components/AppDatePicker.vue';
import { KeyType } from '../interface/tableContentHeaders';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { IDate } from '@/coreDomainsModule/date';

// emits
const emits = defineEmits<{
  (e: 'changeText', text: string): void;
  (e: 'changeDateValue', date: IDate): void;
}>();

//変数
const t = i18n.t;
const locale = computed(() => i18n.locale);

const props = withDefaults(
  defineProps<{
    inputType?: KeyType;
    textValue?: string;
    placeholder?: string;
    format?: string;
    disabledDates?: ((date: Date) => boolean) | Date[];
  }>(),
  {
    inputType: 'string',
    textValue: '',
    placeholder: '入力してください',
    format: 'yyyy-MM-dd',
    /**
     * 指定した日付を選択不可とするかどうかを判定する関数
     */
    disabledDates: () => [],
  }
);

const textValue = ref<string | number>(props.textValue);
const dateValue = ref<Date | null>(null);
//computed
const date = computed({
  /**
   * 値を取得する
   */
  get: () => {
    return dateValue.value;
  },
  /**
   * 値を設定する
   * @param value -値
   */
  set: (value) => {
    // 値に変更があると呼ばれるsetter
    if (!value) {
      return;
    }
    dateValue.value = value;
    emits('changeDateValue', DateService.convertDateToIDate(value));
  },
});

const textComputed = computed({
  /**
   * 値を取得する
   */
  get: () => props.textValue,
  /**
   * 値を設定する
   * @param value -値
   */
  set: (value) => {
    // 呼び出し元に反映させる用
    textValue.value = filterStringNumberUndefinedToString(value);
    emits('changeText', filterStringNumberUndefinedToString(value));
  },
});

// イベント
onMounted(() => {
  textValue.value = props.textValue;
  if (props.inputType === 'IDate') {
    dateValue.value = props.textValue ? DateService.convertDateStrToDateWithSlash(props.textValue) : null;
  }
});

watch(
  () => props.textValue,
  () => {
    if (props.inputType === 'IDate') {
      dateValue.value = props.textValue ? DateService.convertDateStrToDateWithSlash(props.textValue) : null;
    }
  }
);
</script>

<template>
  <div class="editable-cell">
    <template v-if="inputType === 'IDate'">
      <div class="date-picker-component date-field-container__date-picker-component">
        <AppDatePicker
          v-model="date"
          class="date-field__input"
          :mode-height="1"
          :format="format"
          :enable-time-picker="true"
          :month-change-on-scroll="false"
          :teleport="true"
          :disabled-dates="disabledDates"
          week-start="0"
          :locale="locale"
          :placeholder="t('日付を選択')"
          :select-text="t('選択')"
          :cancel-text="t('キャンセル')"
          :show-now-button="true"
          :clearable="false"
          :now-button-label="t('現在を選択')"
          pad
        />
      </div>
    </template>
    <template v-if="inputType === 'string' || inputType === 'number'">
      <div class="text-field-container">
        <input
          v-model="textComputed"
          :type="inputType"
          class="text-input text-field-container__text-input rounded-base-middle"
          :placeholder="t(placeholder)"
        />
      </div>
    </template>
  </div>
</template>
<style lang="scss" scoped>
.editable-cell {
  border: 2px solid #d9d9d9;
}
.text-field-container {
  margin: auto 0;
  max-width: 320px;
  font-size: medium;
  display: grid;
  align-items: center;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr);
  &__label {
    display: flex;
    justify-content: flex-start;
    font-weight: bold;
  }
  .text-input {
    display: block;
    text-align: center;
    line-height: 20px;
    font-size: $fontSizeS;
    margin: 0px 8px;
    padding: 4px 0px;
    &:focus {
      /* outline: 2px solid black; */
      opacity: 90%;
    }
    &:disabled {
      cursor: pointer;
    }
    // スピンボタンを非表示にする
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>

<style lang="scss">
/* - 以下VueDatePickerのCSS操作 - */

@font-face {
  font-family: 'Inter';
  src: url(~@/assets/font/Inter.ttf) format('truetype');
}

/* Inputフィールド全体のレイアウト */
.dp__input {
  width: 100%;
  height: 28px;
  display: block;
  padding: 0;
  border-radius: 6px;
  font-family: 'Inter';
  font-size: $fontSizeS;
  font-weight: 400;
  letter-spacing: 0em;
  text-align: center;
}

/* NOTE: flexにより文字がボタンに収まらない問題を修正する */
.dp__action_buttons {
  flex: none;
}

/* カレンダーアイコンは不要なので削除 */
.dp__input_icon {
  display: none;
}

/* Selectボタンが表示されない問題を修正する */
.dp__action_select {
  background-color: var(--dp-primary-color) !important;
}
</style>
